import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { type GridPosition, type SelectionRange } from '../../../machine/state/types';
import { hasActiveEdit } from '../../../machine/utils/has-active-edit';
import { isSameCell } from '../../../machine/utils/is-same-cell';
import { type TableNavigationAdapter } from '../table-navigation-adapter';
import { isEditableTarget } from '../utils/is-editable-target';

interface TabNavigationBounds {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

/**
 * Creates bounds for the currently navigable table region.
 *
 * Phase three intentionally navigates the full visible table only. Range-bound
 * Tab traversal is deferred until range focus behavior is ready to ship with it.
 */
function createTableBounds(adapter: TableNavigationAdapter): TabNavigationBounds | null {
  const entryCount = adapter.getVisibleEntryCount();
  const fieldCount = adapter.getVisibleFieldIds().length;

  if (entryCount === 0 || fieldCount === 0) {
    return null;
  }

  return {
    minRow: 0,
    maxRow: entryCount - 1,
    minCol: 0,
    maxCol: fieldCount - 1,
  };
}

/**
 * Returns whether the active cell selection covers more than one cell.
 */
function isMultiCellSelection(range: SelectionRange) {
  return (
    range.anchor.row.index !== range.lead.row.index ||
    range.anchor.col.index !== range.lead.col.index
  );
}

/**
 * Keeps keyboard navigation anchored inside the currently visible table bounds.
 */
function clampPosition(
  position: GridPosition,
  bounds: TabNavigationBounds
): GridPosition {
  return {
    row: {
      index: Math.max(bounds.minRow, Math.min(bounds.maxRow, position.row.index)),
    },
    col: {
      index: Math.max(bounds.minCol, Math.min(bounds.maxCol, position.col.index)),
    },
  };
}

/**
 * Moves like spreadsheet Tab: next column, then first column of the next row.
 */
function moveForward(position: GridPosition, bounds: TabNavigationBounds): GridPosition {
  if (position.col.index < bounds.maxCol) {
    return {
      row: position.row,
      col: { index: position.col.index + 1 },
    };
  }

  if (position.row.index < bounds.maxRow) {
    return {
      row: { index: position.row.index + 1 },
      col: { index: bounds.minCol },
    };
  }

  return {
    row: { index: bounds.minRow },
    col: { index: bounds.minCol },
  };
}

/**
 * Moves like spreadsheet Shift+Tab: previous column, then last column of the
 * previous row.
 */
function moveBackward(position: GridPosition, bounds: TabNavigationBounds): GridPosition {
  if (position.col.index > bounds.minCol) {
    return {
      row: position.row,
      col: { index: position.col.index - 1 },
    };
  }

  if (position.row.index > bounds.minRow) {
    return {
      row: { index: position.row.index - 1 },
      col: { index: bounds.maxCol },
    };
  }

  return {
    row: { index: bounds.maxRow },
    col: { index: bounds.maxCol },
  };
}

export function createTableTabNavigationKeyDownHandler(adapter: TableNavigationAdapter) {
  /**
   * Handles table Tab traversal without letting focus escape to legacy controls.
   *
   * Edit mode is committed before moving selection, editable non-table targets
   * are ignored, and multi-cell selections consume Tab without movement until
   * range-bounded traversal is introduced.
   */
  return function handleTableTabNavigationKeyDown(
    capture: KeyboardDownCapture,
    machine: SelectionsMachine
  ): void {
    if (capture.properties.key !== 'Tab') {
      return;
    }

    const state = machine.getState();
    const activeEdit = hasActiveEdit(state) ? state.activeEdit : null;

    if (activeEdit && activeEdit.editingContext !== 'table') {
      return;
    }

    if (!activeEdit && isEditableTarget(capture.properties.event.target)) {
      return;
    }

    const focusedCell = state.focusedCell ?? activeEdit?.position;

    if (!focusedCell || state.mode === 'idle') {
      return;
    }

    const tableBounds = createTableBounds(adapter);

    if (!tableBounds) {
      return;
    }

    const activeSelection =
      state.mode === 'cell' ? state.selections[state.selections.length - 1] : undefined;

    if (!activeEdit && activeSelection && isMultiCellSelection(activeSelection)) {
      capture.properties.event.preventDefault();
      capture.properties.consumed = true;

      return;
    }

    const origin = clampPosition(focusedCell, tableBounds);
    const target = capture.properties.event.shiftKey
      ? moveBackward(origin, tableBounds)
      : moveForward(origin, tableBounds);

    capture.properties.event.preventDefault();

    if (activeEdit) {
      machine.dispatch({ type: 'stopEditing', source: 'keyboard' });
    }

    if (!isSameCell(origin, target)) {
      machine.dispatch({
        type: 'selectCell',
        cell: target,
        source: 'keyboard',
      });
    }

    capture.properties.consumed = true;
  };
}
